import { z } from "zod";
import { IdSchema, IsoDateTimeSchema } from "./common.js";

/**
 * Realtime contract — design §24.6.
 * Transport: SSE for progress/token streaming, REST for snapshot recovery.
 * Every event carries `sequence` so a client that detects a gap re-fetches the
 * timeline snapshot instead of guessing.
 */
export const PROGRESS_EVENT_SCHEMA_VERSION = "1";

export const ProgressEventTypeSchema = z.enum([
	"EXECUTION_STARTED",
	"EXECUTION_STATUS_CHANGED",
	"STAGE_STATUS_CHANGED",
	"NODE_STATUS_CHANGED",
	"ATTEMPT_STARTED",
	"ATTEMPT_FINISHED",
	"SUBTASK_EVENT",
	"GATE_EVALUATED",
	"WAIT_STARTED",
	"WAIT_RESOLVED",
	"ARTIFACT_COMMITTED",
	"CHECKPOINT_COMMITTED",
	"PROVIDER_INVOCATION_RECORDED",
	"COST_RECORDED",
	"EXECUTION_COMPLETED",
	"HEARTBEAT",
]);
export type ProgressEventType = z.infer<typeof ProgressEventTypeSchema>;

export const ProgressEventSchema = z.object({
	eventId: IdSchema,
	schemaVersion: z.literal(PROGRESS_EVENT_SCHEMA_VERSION),
	sequence: z.number().int().min(0),
	eventType: ProgressEventTypeSchema,
	tenantId: IdSchema,
	executionId: IdSchema.nullable(),
	sessionId: IdSchema.nullable(),
	entityType: z.string(),
	entityId: IdSchema.nullable(),
	fromStatus: z.string().nullable(),
	toStatus: z.string().nullable(),
	message: z.string(),
	correlationId: z.string(),
	traceId: z.string(),
	occurredAt: IsoDateTimeSchema,
});
export type ProgressEvent = z.infer<typeof ProgressEventSchema>;

/** REST fallback for clients that cannot hold an SSE connection. */
export const ProgressEventPollResponseSchema = z.object({
	events: z.array(ProgressEventSchema),
	lastSequence: z.number().int().min(0),
	hasMore: z.boolean(),
});
export type ProgressEventPollResponse = z.infer<typeof ProgressEventPollResponseSchema>;

/** Encodes one event into an SSE frame. */
export function toSseFrame(event: ProgressEvent): string {
	return `id: ${event.sequence}\nevent: ${event.eventType}\ndata: ${JSON.stringify(event)}\n\n`;
}
